import React, { useState, useEffect, useCallback } from 'react';
import { getOeeDashboard } from '../api';
import { useTranslation } from '../i18n/LanguageContext';
import OeeExecutive from './OeeExecutive';
import OeeWaterfall from './OeeWaterfall';
import OeeHeatmap from './OeeHeatmap';
import OeeInitiatives from './OeeInitiatives';
import OeeScenarios from './OeeScenarios';
import OeePilot from './OeePilot';
import OeePortfolio from './OeePortfolio';

const OEE_TAB_IDS = ['executive', 'waterfall', 'heatmap', 'initiatives', 'scenarios', 'portfolio', 'pilot'];

export default function OeeModule({ trackFeature }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState('executive');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const handleTabChange = (tabId) => {
    setTab(tabId);
    if (trackFeature) trackFeature('oee', `oee_tab_${tabId}`, tabId);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getOeeDashboard();
      setData(res.data);
    } catch (err) {
      console.error('Failed to load OEE data', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse" data-testid="oee-loading">
        <div className="h-12 bg-surface border border-border rounded-lg w-full" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="h-28 bg-surface border border-border rounded-lg" />
          ))}
        </div>
        <div className="h-64 bg-surface border border-border rounded-lg" />
      </div>
    );
  }

  const renderTab = () => {
    if (!data) return null;
    switch (tab) {
      case 'executive': return <OeeExecutive data={data} />;
      case 'waterfall': return <OeeWaterfall data={data} />;
      case 'heatmap': return <OeeHeatmap data={data} />;
      case 'initiatives': return <OeeInitiatives data={data} />;
      case 'scenarios': return <OeeScenarios data={data} />;
      case 'portfolio': return <OeePortfolio data={data} />;
      case 'pilot': return <OeePilot data={data} />;
      default: return null;
    }
  };

  return (
    <div className="space-y-5" data-testid="oee-module">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-text-primary">
          {t('oee.title')}
        </h1>
        <p className="text-sm font-body text-text-secondary mt-1">
          {t('oee.subtitle')}
        </p>
      </div>

      <div className="flex gap-1 overflow-x-auto pb-1 bg-surface border border-border rounded-lg p-1" data-testid="oee-tabs">
        {OEE_TAB_IDS.map((id) => (
          <button
            key={id}
            data-testid={`oee-tab-${id}`}
            onClick={() => handleTabChange(id)}
            className={`
              whitespace-nowrap px-4 py-2 rounded-md text-sm font-body font-medium transition-all duration-200
              ${tab === id
                ? 'bg-primary text-white shadow-sm'
                : 'text-text-secondary hover:bg-white/5 hover:text-text-primary'
              }
            `}
          >
            {t(`oee.tabs.${id}`)}
          </button>
        ))}
      </div>

      {renderTab()}
    </div>
  );
}
